/**
 * TcmEnablementPanel — per-tenant enablement wizard for the continuous watch.
 *
 * Five-state machine UX driven by `TcmEnablementState`:
 *   NotEnabled → ScriptGenerated → VerificationPending → Enabled
 *                                                       → ExoProvisioning
 *                                                       → Error
 *
 * Actions wired to `tcmApi`:
 *   - generateEnablementScript: returns text/plain PowerShell. We surface
 *     it inline in a Carbon code snippet with its copy button.
 *   - verifyEnablement: requires a TcmEnablementVerifyRequest payload the
 *     customer admin produces by running the script. For the lab v1 we
 *     surface a "Paste verification payload" text area — Platform Admin
 *     pastes the JSON the script printed at step 10.
 *   - toggleShadowEnable: gates ingestion per-tenant. Independent of
 *     `Tenants.BaselineSource` per AD-S04 / codex finding 1 (reserved
 *     for cutover). Only callable once the tenant is Enabled (or
 *     ExoProvisioning — Graph-only ingestion is still meaningful).
 *
 * Carbon: one layer-01 tile (joined to the lab's selected tab) with the state
 * title and its tag, the metadata as a 1px-gutter grid, the actions row (one
 * Soft Green primary for the state's next step, tertiary for the rest), info
 * notifications in place of the old sand callout bands, the script in a code
 * snippet, and the promote confirmation as a Carbon confirm dialog.
 *
 * Cache key shape mirrors the Admin Portal hooks: ['tcm', 'enablement',
 * tenantId]. Mutations invalidate the matching status query on success so
 * the UI reads fresh state without manual refetch.
 *
 * Voice: UI copy never names the vendor pipeline (blueprint §5.1 rule 7); the
 * Microsoft monitor pipeline is "the continuous watch" and the PowerShell
 * export "the baseline scan".
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { Button } from '@/components/carbon/Button'
import { CodeSnippet } from '@/components/carbon/CodeSnippet'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Skeleton } from '@/components/carbon/Skeleton'
import { TextArea } from '@/components/carbon/TextArea'
import { Tile } from '@/components/carbon/Tile'
import type { ButtonKind } from '@/components/carbon/types'
import { api, tcmApi } from '@/lib/api'
import { getServerMessage } from '@/lib/api-error'
import type {
  TcmCapacityScan,
  TcmEnablementState,
  TcmEnablementStatus,
  TcmEnablementVerifyRequest,
  TcmEnablementVerifyResult,
} from '@/types/tcm'
import type { Tenant } from '@/types'

import TcmEnablementStatusBadge from './TcmEnablementStatusBadge'
import { describeCapacityForLab } from './tcmCapacityLab'

interface TcmEnablementPanelProps {
  tenantId: string
}

// Panel titles, one per state (from the mockup's "State titles", with the
// vendor pipeline renamed).
const STATE_TITLES: Record<TcmEnablementState, string> = {
  NotEnabled: 'No watch service principal in this tenant yet.',
  ScriptGenerated: 'Awaiting customer admin to run the enablement script.',
  VerificationPending: 'Checking service principals, app roles and Exchange RBAC against Graph.',
  Enabled: 'Every watched workload is operational.',
  ExoProvisioning:
    'Graph side complete. Exchange management-role RBAC is still finishing, so Exchange recipient reads are pending.',
  Error: 'Verification failed.',
}

const ENABLEMENT_KEY = (tenantId: string) =>
  ['tcm', 'enablement', tenantId] as const

export default function TcmEnablementPanel({ tenantId }: TcmEnablementPanelProps) {
  const queryClient = useQueryClient()
  const [generatedScript, setGeneratedScript] = useState<string | null>(null)
  const [verifyPayloadJson, setVerifyPayloadJson] = useState('')
  // Wave 7 — confirmation dialog state for promote-to-hybrid.
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false)

  // Read the cached tenant row so we know:
  //   1. The customer-facing tenant name (for the confirmation copy).
  //   2. The current baselineSource — the button is hidden when the
  //      tenant is already 'Hybrid' so a Platform Admin can't double-
  //      promote.
  // useTenantContext's cache key ('tenants') is shared with the
  // sidebar; reading the same key here doesn't introduce a new query
  // and stays consistent with the rest of the app.
  const { data: tenantsList } = useQuery<Tenant[]>({
    queryKey: ['tenants'],
    queryFn: api.getTenants,
    staleTime: 5 * 60 * 1000,
  })
  const tenant = tenantsList?.find(t => t.tenantId === tenantId)
  const tenantName = tenant?.name ?? tenantId
  const tenantBaselineSource = tenant?.baselineSource ?? 'DSC'

  // Status query — Platform Admin endpoint, gated server-side. The hook
  // refetches on mount + every 30s (lab feedback loop is short).
  const statusQuery = useQuery<TcmEnablementStatus>({
    queryKey: ENABLEMENT_KEY(tenantId),
    queryFn: () => tcmApi.getEnablementStatus(tenantId),
    refetchInterval: 30_000,
  })

  // Explicit generics — react-query's inference treats `Promise<any>` as
  // `unknown` in onSuccess, which then poisons the toast/setter call sites.
  // Pinning <TData, TError, TVariables> at the hook keeps the onSuccess
  // parameter strongly typed even if the underlying api.ts signature loosens.
  const generateMutation = useMutation<string, Error, void>({
    mutationFn: () => tcmApi.generateEnablementScript(tenantId),
    onSuccess: scriptText => {
      setGeneratedScript(scriptText)
      // The server-side rotates `VerificationSeed` on every call, so the
      // user-facing token (printed in the script at step 1) refreshes on
      // each generation.
      toast.success('Script ready. Its token expires in 24 hours.')
      void queryClient.invalidateQueries({ queryKey: ENABLEMENT_KEY(tenantId) })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Script generation failed.'
      toast.error(msg)
    },
  })

  const verifyMutation = useMutation<
    TcmEnablementVerifyResult,
    Error,
    TcmEnablementVerifyRequest
  >({
    mutationFn: (body: TcmEnablementVerifyRequest) =>
      tcmApi.verifyEnablement(tenantId, body),
    onSuccess: result => {
      if (result.isValid && result.status === 'Enabled') {
        toast.success(
          'Enabled. All 20 Graph app-roles granted and EXO RBAC confirmed.',
        )
      } else if (result.status === 'ExoProvisioning') {
        toast.error(
          'Graph side passed, but Exchange management-role RBAC is still finishing. Re-run the script with the Exchange Administrator role once org customization completes. See F-03.',
        )
      } else if (!result.isValid && result.status === 'ScriptGenerated') {
        // Retryable transient probe failure: the HMAC already matched and the
        // payload is sound, so the server PRESERVED the seed and rolled the row
        // back to ScriptGenerated (TcmEnablementService:360-374). The operator
        // should click Verify again with the SAME pasted payload — telling them
        // to re-run/regenerate would needlessly burn a customer-GA round-trip.
        // Prefer the server message; it already carries the precise guidance.
        toast.error(
          result.message ||
            'The watch probe failed transiently. Click Verify again with the same payload, with no need to re-run the script.',
        )
      } else if (!result.isValid && result.status === 'Error') {
        // Genuine integrity failure (token mismatch / missing seed / expired) —
        // the server consumed the seed, so the operator must re-run/regenerate.
        toast.error('Verification token did not match. Re-run the script or regenerate.')
      } else {
        // Edge: server returned a different state we don't have a locked
        // toast for. Use the server-supplied message verbatim so the
        // Platform Admin sees the precise error.
        toast.error(result.message || 'Verification did not complete.')
      }
      void queryClient.invalidateQueries({ queryKey: ENABLEMENT_KEY(tenantId) })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Verification request failed.'
      toast.error(msg)
    },
  })

  const shadowMutation = useMutation<void, Error, boolean>({
    mutationFn: (enabled: boolean) =>
      tcmApi.toggleShadowEnable(tenantId, { enabled }),
    onSuccess: (_data, enabled) => {
      toast.success(enabled ? 'Shadow ingestion enabled.' : 'Shadow ingestion disabled.')
      void queryClient.invalidateQueries({ queryKey: ENABLEMENT_KEY(tenantId) })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Shadow toggle failed.'
      toast.error(msg)
    },
  })

  const purviewMutation = useMutation<void, Error, boolean>({
    mutationFn: (enabled: boolean) =>
      tcmApi.togglePurviewComplianceEnable(tenantId, { enabled }),
    onSuccess: (_data, enabled) => {
      toast.success(enabled ? 'Purview compliance monitoring enabled.' : 'Purview compliance monitoring disabled.')
      void queryClient.invalidateQueries({ queryKey: ENABLEMENT_KEY(tenantId) })
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Purview compliance toggle failed.'
      toast.error(msg)
    },
  })

  // Wave 7 — promote-to-Hybrid flips Tenants.BaselineSource server-side
  // (pre-flight gated) and invalidates the four tenant-shaped cache
  // keys. Refetching ['tenants'] here updates every consumer of
  // useTenantContext — the /configuration-drift page picks up the new
  // ManagementTab on its next render.
  const promoteMutation = useMutation<
    { tenantId: string; baselineSource: string },
    Error,
    void
  >({
    mutationFn: () => tcmApi.promoteToHybrid(tenantId),
    onSuccess: result => {
      toast.success(
        `${tenantName} is now Hybrid. Customer users will see continuous-watch drift.`,
      )
      setShowPromoteConfirm(false)
      void queryClient.invalidateQueries({ queryKey: ['tenants'] })
      void queryClient.invalidateQueries({ queryKey: ENABLEMENT_KEY(tenantId) })
      // Eagerly write the new value into the tenants cache so the
      // /configuration-drift page reads the new baselineSource on its
      // next render without waiting for the refetch.
      queryClient.setQueryData<Tenant[]>(['tenants'], prev => {
        if (!prev) return prev
        return prev.map(t =>
          t.tenantId === tenantId
            ? { ...t, baselineSource: result.baselineSource }
            : t,
        )
      })
    },
    onError: (err: unknown) => {
      // 409 from the server carries a meaningful pre-flight reason —
      // surface it verbatim. Don't translate or eat the error.
      const msg = getServerMessage(
        err,
        'Promotion did not land. Check the server log.',
      )
      toast.error(msg)
    },
  })

  if (statusQuery.isLoading) {
    return (
      <Tile padding={24} className="min-w-0">
        <Skeleton lines={3} label="Loading enablement status" className="max-w-[640px]" />
      </Tile>
    )
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <InlineNotification kind="error" title="Status unavailable.">
        {statusQuery.error instanceof Error
          ? statusQuery.error.message
          : 'Could not load the enablement status for this tenant.'}
      </InlineNotification>
    )
  }

  const status = statusQuery.data

  // Parse JSON payload only when the user submits — keeps the text area
  // free of premature validation noise. Invalid JSON is surfaced as an
  // error toast.
  const handleVerify = () => {
    let parsed: TcmEnablementVerifyRequest
    try {
      const raw = JSON.parse(verifyPayloadJson)
      // The script prints `{ payload, token }` — we accept both that
      // shape and a bare payload (older script outputs). Either way the
      // server demands both fields, so reject early if missing.
      if (raw && typeof raw === 'object' && 'payload' in raw && 'token' in raw) {
        parsed = raw as TcmEnablementVerifyRequest
      } else {
        throw new Error('Payload missing "payload" or "token" field.')
      }
    } catch (e) {
      toast.error(
        e instanceof Error
          ? `Could not parse verification payload: ${e.message}`
          : 'Could not parse verification payload.',
      )
      return
    }
    verifyMutation.mutate(parsed)
  }

  // Shadow toggle only meaningful once the tenant is Enabled or
  // ExoProvisioning (Graph-only). Hidden in every other state.
  const canToggleShadow = status.status === 'Enabled' || status.status === 'ExoProvisioning'
  const canTogglePurview = status.status === 'Enabled' || status.status === 'ExoProvisioning'

  // Verify payload paste-and-submit. ExoProvisioning is included so a staffer
  // who reached it via the skip path can submit the follow-up (run-2) verify
  // once EXO RBAC lands — the seed is kept live on that state (R1), so
  // re-verifying in place works without regenerating a script (which would
  // reset the status).
  const showVerify =
    status.status === 'ScriptGenerated' ||
    status.status === 'VerificationPending' ||
    status.status === 'ExoProvisioning' ||
    status.status === 'Error'

  // Wave 7 — Promote to Hybrid. Only offered once the tenant is Enabled AND
  // not already Hybrid. The server enforces the rest of the pre-flight (two
  // ingestion cycles + active monitor version) and surfaces 409 with a
  // meaningful message if anything's missing.
  const showPromote = status.status === 'Enabled' && tenantBaselineSource !== 'Hybrid'

  // One Soft Green button per view: the state's next step. Generating the
  // script leads until a script exists; then verifying; then promoting.
  const generateKind: ButtonKind = status.status === 'NotEnabled' ? 'primary' : 'tertiary'

  const headingId = `tcm-enablement-${tenantId}-heading`

  return (
    <section aria-labelledby={headingId} className="min-w-0">
      <Tile padding={24} className="min-w-0">
        {/* Header — the state title and its tag. */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h2 id={headingId} className="m-0 min-w-0 max-w-[48ch] text-heading font-normal text-text-primary">
            {STATE_TITLES[status.status]}
          </h2>
          <TcmEnablementStatusBadge status={status.status} />
        </div>

        {/* Metadata grid — 1px gutters. */}
        <dl className="m-0 mt-6 grid grid-cols-1 gap-px border border-line-subtle bg-line-subtle c-md:grid-cols-2 c-lg:grid-cols-3 c-xlg:grid-cols-4">
          <Meta k="Tenant" v={tenantId} mono />
          <Meta k="Watch service principal" v={status.tcmSpObjectId ?? '—'} mono />
          <Meta k="Exchange RBAC" v={status.exoRbacConfigured ? 'Configured' : 'Missing'} />
          <Meta k="Shadow ingestion" v={status.shadowEnabled ? 'Enabled' : 'Disabled'} />
          <Meta k="Purview compliance" v={status.purviewComplianceEnabled ? 'Enabled' : 'Disabled'} />
          <Meta k="Script generated" v={fmtDate(status.scriptGeneratedAt)} />
          <Meta k="Verified at" v={fmtDate(status.verifiedAt)} />
          <Meta k="Enabled at" v={fmtDate(status.enabledAt)} />
          <Meta k="Last error" v={status.lastErrorMessage ?? '—'} />
        </dl>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap gap-4 max-c-md:[&>*]:w-full max-c-md:[&>*]:max-w-none">
          <Button kind={generateKind} onClick={() => generateMutation.mutate()} loading={generateMutation.isPending}>
            {generateMutation.isPending ? 'Generating…' : 'Generate script'}
          </Button>
          {canToggleShadow && (
            <Button
              kind="tertiary"
              onClick={() => shadowMutation.mutate(!status.shadowEnabled)}
              loading={shadowMutation.isPending}
            >
              {shadowMutation.isPending
                ? 'Saving…'
                : status.shadowEnabled
                  ? 'Disable shadow ingest'
                  : 'Enable shadow ingest'}
            </Button>
          )}
          {canTogglePurview && (
            <Button
              kind="tertiary"
              onClick={() => purviewMutation.mutate(!status.purviewComplianceEnabled)}
              loading={purviewMutation.isPending}
            >
              {purviewMutation.isPending
                ? 'Saving…'
                : status.purviewComplianceEnabled
                  ? 'Disable Purview compliance'
                  : 'Enable Purview compliance'}
            </Button>
          )}
          <Button
            kind="ghost"
            onClick={() => void queryClient.invalidateQueries({ queryKey: ENABLEMENT_KEY(tenantId) })}
          >
            Refresh status
          </Button>
        </div>

        {/* Purview compliance grants a WRITE-capable role — call it out so the
            operator knows toggling here is not the whole story: the generated
            enablement script changes, and it must be re-run to actually
            grant/revoke the Compliance Administrator role on the watch's
            service principal. */}
        {canTogglePurview && (
          <InlineNotification kind="info" title="Purview compliance grants a write-capable role." className="mt-6">
            It gives the watch service principal the Compliance Administrator role, a heavier grant than the read-only
            roles. Toggling here records your intent and rewrites the enablement script. Regenerate and re-run it so
            the role is actually granted or revoked in the tenant.
          </InlineNotification>
        )}

        {/* Exchange provisioning notice — mirrors the mockup callout. */}
        {status.status === 'ExoProvisioning' && (
          <InlineNotification kind="info" title="Exchange RBAC is required (F-03)." className="mt-4">
            Graph app roles do not authorize cmdlet-backed Exchange reads. Re-run the enablement script in a session
            with the Exchange Administrator role attached; steps 5 to 8 add the missing RBAC.
          </InlineNotification>
        )}

        {/* Generated script, with its copy button. */}
        {generatedScript && (
          <div className="mt-6 min-w-0">
            <h3 className="m-0 mb-2 text-body font-semibold text-text-primary">Enablement script</h3>
            <CodeSnippet
              kind="multi"
              code={generatedScript}
              language="powershell"
              maxHeight="24rem"
              copyLabel="Copy the enablement script"
              className="border border-line-subtle"
            />
          </div>
        )}

        {showVerify && (
          <div className="mt-6 min-w-0 max-w-[720px]">
            <TextArea
              id={`tcm-verify-payload-${tenantId}`}
              label="Paste verification payload"
              helper="The JSON the script printed at its last step."
              value={verifyPayloadJson}
              onChange={e => setVerifyPayloadJson(e.target.value)}
              rows={6}
              placeholder='{"payload":{...},"token":"..."}'
              spellCheck={false}
              className="font-mono text-[13px]"
            />
            <Button
              kind="primary"
              className="mt-4"
              onClick={handleVerify}
              disabled={verifyPayloadJson.trim().length === 0}
              loading={verifyMutation.isPending}
            >
              {verifyMutation.isPending ? 'Verifying…' : 'Verify setup'}
            </Button>
          </div>
        )}

        {/* Wave 7 — the cutover. The button is the affordance; the
            confirmation dialog carries the consequence copy. */}
        {showPromote && (
          <div className="mt-8 flex flex-wrap items-start justify-between gap-4 border-t border-line-subtle pt-6">
            <div className="min-w-0 max-w-[64ch]">
              <h3 className="m-0 text-body font-semibold text-text-primary">Promote to Hybrid</h3>
              <p className="m-0 mt-1 text-body text-text-secondary">
                Customer users on <b className="font-semibold text-text-primary">{tenantName}</b> start seeing
                continuous-watch drift in their drift queue. SharePoint and OneDrive stay on the baseline scan.
              </p>
            </div>
            <Button
              kind="primary"
              onClick={() => setShowPromoteConfirm(true)}
              loading={promoteMutation.isPending}
              className="max-c-md:w-full max-c-md:max-w-none"
            >
              {promoteMutation.isPending ? 'Promoting…' : 'Promote to Hybrid'}
            </Button>
          </div>
        )}

        {/* Already-Hybrid note — keeps the panel honest after the cutover.
            No CTA; the data IS the verdict. */}
        {tenantBaselineSource === 'Hybrid' && (
          <div className="mt-8 border-t border-line-subtle pt-6">
            <h3 className="m-0 text-body font-semibold text-text-primary">Running Hybrid</h3>
            <p className="m-0 mt-1 max-w-[72ch] text-body text-text-secondary">
              <b className="font-semibold text-text-primary">{tenantName}</b> is running Hybrid. The continuous watch
              covers Entra, Exchange, Intune, Teams, Defender and Purview; the baseline scan keeps SharePoint and
              OneDrive.
            </p>
          </div>
        )}
      </Tile>

      {/* Capacity scan (large-client wave 3, H-8/H-9): the verdict before
          enabling or promoting — licence tier, the tenant's own monitors, the
          room left for Guardian's monitor, per-profile sizing and the next
          sweep. Read-only; never creates a snapshot. */}
      <CapacityTile tenantId={tenantId} />

      <ConfirmDialog
        open={showPromoteConfirm}
        title={`Promote ${tenantName} to Hybrid?`}
        body={
          <>
            This moves <b className="font-semibold text-text-primary">{tenantName}</b> from scan-only to Hybrid: the
            continuous watch takes every workload except SharePoint and OneDrive, which stay on the baseline scan.
            Customer users on this tenant will see continuous-watch drift on the Baseline Compliance page.
          </>
        }
        confirmLabel={promoteMutation.isPending ? 'Promoting…' : 'Promote'}
        loading={promoteMutation.isPending}
        onConfirm={() => promoteMutation.mutate()}
        onCancel={() => setShowPromoteConfirm(false)}
      />
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Capacity tile                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The capacity verdict tile (wave 3, H-8/H-9). Owns its own query so the
 * panel's early returns never reorder hooks. All copy is composed by
 * describeCapacityForLab (pure, vitest-pinned); this is layout only. The
 * server caches the scan for five minutes; "Scan again" re-reads Graph.
 */
function CapacityTile({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient()
  const capacityQuery = useQuery<TcmCapacityScan>({
    queryKey: ['tcm-capacity', tenantId],
    queryFn: () => tcmApi.getCapacityScan(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
  const rescanMutation = useMutation<TcmCapacityScan, Error, void>({
    mutationFn: () => tcmApi.getCapacityScan(tenantId, true),
    onSuccess: fresh => queryClient.setQueryData(['tcm-capacity', tenantId], fresh),
    onError: (err: unknown) => toast.error(getServerMessage(err, 'The capacity scan did not run. Try again.')),
  })
  const view = describeCapacityForLab(capacityQuery.data, capacityQuery.isError)
  const titleId = `tcm-capacity-${tenantId}-title`

  return (
    <Tile as="section" padding={24} className="mt-8 min-w-0" aria-labelledby={titleId}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <h3 id={titleId} className="m-0 text-body font-semibold text-text-primary">
          Capacity
        </h3>
        <Button kind="ghost" size="sm" onClick={() => rescanMutation.mutate()} loading={rescanMutation.isPending}>
          {rescanMutation.isPending ? 'Scanning…' : 'Scan again'}
        </Button>
      </div>

      {view.state === 'loading' ? (
        <Skeleton lines={2} label="Reading licenses, monitors and snapshot jobs" className="mt-4 max-w-[640px]" />
      ) : (
        <>
          <p className="m-0 mt-2 text-heading font-normal text-text-primary">{view.headline}</p>
          {view.detail && <p className="m-0 mt-1 max-w-[72ch] text-body text-text-secondary">{view.detail}</p>}
        </>
      )}

      {view.rows.length > 0 && (
        <dl className="m-0 mt-6 grid grid-cols-1 gap-px border border-line-subtle bg-line-subtle c-md:grid-cols-2 c-lg:grid-cols-3">
          {view.rows.map(row => (
            <Meta key={row.label} k={row.label} v={row.value} />
          ))}
        </dl>
      )}

      {view.profiles.length > 0 && (
        <>
          <h4 className="m-0 mt-6 text-body font-semibold text-text-primary">Profiles sized from the latest capture</h4>
          <dl className="m-0 mt-2 grid grid-cols-1 gap-px border border-line-subtle bg-line-subtle c-md:grid-cols-2 c-lg:grid-cols-3">
            {view.profiles.map(row => (
              <Meta key={row.label} k={row.label} v={row.value} />
            ))}
          </dl>
        </>
      )}
    </Tile>
  )
}

/* -------------------------------------------------------------------------- */
/* Local helpers                                                              */
/* -------------------------------------------------------------------------- */

interface MetaProps {
  k: string
  v: string
  mono?: boolean
}

// One label/value cell of the metadata grid. Values carry no hue: the state
// tag above and the words themselves say good or bad (the Neutral Bad State
// Rule).
function Meta({ k, v, mono }: MetaProps) {
  return (
    <div className="min-w-0 bg-layer-1 p-4">
      <dt className="text-helper text-text-helper">{k}</dt>
      <dd
        className={`m-0 mt-1 text-body text-text-primary [overflow-wrap:anywhere] ${mono ? 'font-mono text-[13px]' : ''}`}
        title={v}
      >
        {v}
      </dd>
    </div>
  )
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString()
  } catch {
    return iso
  }
}
